import { useEffect, useState, useRef, Suspense, lazy, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

// --- MODULAR COMPONENT IMPORTS ---
import GlobalStyles from './components/GlobalStyles.jsx';
import Toast from './components/Toast.jsx';

// --- CONFIG & STATE ---
import { DEFAULT_CONFIG } from './config/defaultConfig.js';
import { mergeConfig } from './utils/configMerger.js';
import { saveToLocalVault, getLocalVaultSnapshots } from './utils/backupVault.js';
import { consumeEditQuota, redeemAdminPasscode } from './components/dashboard/EditQuotaManager.jsx';
import { sanitizeConfigData, isPlaceholderText } from './utils/helpers.js';
import {
    authHeaders,
    normalizeAppUrl,
    smoothScrollTo,
    getAppBase,
    getTenantHandle,
    getTenantStorageKey,
    getApiUrl
} from './utils/styleHelpers.js';

// --- E-COMMERCE IMPORTS (LAZY LOADED FOR OPTIMAL BUNDLE SIZE) ---
import { CartProvider } from './ecommerce/CartState';
import { CurrencyProvider } from './ecommerce/CurrencyState';
import { EcommercePaywall, isEcommerceUnlocked } from './ecommerce';

// --- i18n MULTILANGUAGE MODULE (OPTIONAL — MODULE GATED) ---
import { LanguageProvider } from './i18n';
import { AgeVerificationOverlay } from './components/AgeVerificationOverlay.jsx';

const safeLazy = (importFn) => lazy(() => 
    importFn().catch(err => {
        const str = String((err && (err.message || err.stack || err)) || '').toLowerCase();
        const isChunkError = 
            str.includes('failed to fetch') || 
            str.includes('loading chunk') || 
            str.includes('module script') || 
            str.includes('chunkloaderror') ||
            str.includes('expected a javascript-or-wasm module script');
        if (isChunkError && typeof window !== 'undefined') {
            const now = Date.now();
            const lastReload = parseInt(sessionStorage.getItem('omnivaso_chunk_reload_lock') || '0', 10);
            if (now - lastReload > 10000) {
                sessionStorage.setItem('omnivaso_chunk_reload_lock', String(now));
                console.warn('⚡ Missing deployment chunk detected — auto-reloading page...');
                window.location.reload();
                return new Promise(() => {});
            }
        }
        throw err;
    })
);

const LandingPage = safeLazy(() => import('./pages/LandingPage.jsx'));
const PageView = safeLazy(() => import('./pages/PageView.jsx'));
const NotFoundPage = safeLazy(() => import('./components/NotFoundPage.jsx'));
const ArticlesPage = safeLazy(() => import('./pages/ArticlesPage.jsx'));
const ArticleView = safeLazy(() => import('./pages/ArticleView.jsx'));
const ShopPage = safeLazy(() => import('./ecommerce/ShopPage.jsx'));
const ProductPage = safeLazy(() => import('./ecommerce/ProductPage.jsx'));
const CartPage = safeLazy(() => import('./ecommerce/CartPage.jsx'));
const SearchPage = safeLazy(() => import('./components/SearchPage.jsx'));
const PromotionalPage = safeLazy(() => import('./pages/PromotionalPage.jsx'));
const EventsPage = safeLazy(() => import('./pages/EventsPage.jsx').then(m => ({ default: m.EventsPage })));
const TicketingPaywall = safeLazy(() => import('./components/events/TicketingPaywall.jsx').then(m => ({ default: m.TicketingPaywall })));
const ArtworkCatalogView = safeLazy(() => import('./artwork/ArtworkCatalogView.jsx'));
const ArtworkPaywall = safeLazy(() => import('./artwork/ArtworkPaywall.jsx'));
const OnlineViewingRoomView = safeLazy(() => import('./artwork/OnlineViewingRoomView.jsx'));
const ViewingRoomIndexPage = safeLazy(() => import('./artwork/ViewingRoomIndexPage.jsx'));
const WallScalerModal = safeLazy(() => import('./artwork/WallScalerModal.jsx'));
const LoginPage = safeLazy(() => import('./pages/LoginPage.jsx'));
const AgencyLoginPage = safeLazy(() => import('./pages/AgencyLoginPage.jsx'));
const AdminDashboard = safeLazy(() => import('./pages/AdminDashboard.jsx'));
const SearchTool = safeLazy(() => import('./components/SearchTool.jsx'));
const SubdomainProvisioner = safeLazy(() => import('./components/SubdomainProvisioner.jsx'));
const OrderLookupModal = safeLazy(() => import('./components/OrderLookupModal.jsx'));
import { isTicketingUnlocked } from './components/events/EventTicketingManager.jsx';
import { isArtworkCatalogUnlocked } from './artwork/artworkModel.js';

// --- ROUTE REDIRECT HELPERS ---
const TrackOrderRoute = ({ setIsOrderLookupOpen }) => {
    useEffect(() => {
        setIsOrderLookupOpen(true);
    }, [setIsOrderLookupOpen]);
    return (
        <div style={{ minHeight: '60vh', padding: '60px 20px', textAlign: 'center', fontFamily: 'sans-serif' }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📦</div>
            <h2 style={{ color: '#0f172a' }}>Order Tracking</h2>
            <p style={{ color: '#64748b' }}>Opening order lookup modal...</p>
        </div>
    );
};

const CustomerAccountRedirect = () => {
    const navigate = useNavigate();
    useEffect(() => {
        navigate('/shop?orders=true', { replace: true });
    }, [navigate]);
    return null;
};

const ScrollToTop = () => {
    const { pathname, hash, state } = useLocation();
    const prevPathRef = useRef(pathname);

    useEffect(() => {
        const prevPath = prevPathRef.current;
        prevPathRef.current = pathname;

        // If explicitly requested not to reset scroll (e.g., category filtering), remain still
        if (state?.preventScrollReset || state?.keepScroll || state?.noScroll) {
            return;
        }

        // If anchor hash is present, let anchor scrolling handle it
        if (hash) {
            const id = hash.replace(/^#/, '');
            smoothScrollTo(id);
            return;
        }

        // Check if navigating within the shop catalog (category / subcategory switching)
        const isShopCatalog = (p) => {
            if (!p) return false;
            const clean = p.replace(/\/$/, '');
            return /(?:^|\/)(shop|products|category)(\/.*)?$/i.test(clean);
        };

        if (isShopCatalog(prevPath) && isShopCatalog(pathname)) {
            // Category navigation within the shop - remain still and do not jump to top
            return;
        }

        // Only scroll to top on actual page navigation (pathname change)
        if (prevPath !== pathname) {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
    }, [pathname, hash, state]);

    return null;
};

class RootErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        const msg = (error && error.message) || '';
        const stack = (error && error.stack) || '';
        const isExternalTelemetryError = 
            msg.includes('startTime') || 
            msg.includes('reportAllChanges') || 
            stack.includes('reportAllChanges') ||
            stack.includes('et.reportAllChanges');
        if (isExternalTelemetryError) {
            return { hasError: false, error: null };
        }

        const lower = (msg + ' ' + stack).toLowerCase();
        const isChunkError = 
            lower.includes('failed to fetch') || 
            lower.includes('loading chunk') || 
            lower.includes('module script') || 
            lower.includes('chunkloaderror') ||
            lower.includes('expected a javascript-or-wasm module script');
        if (isChunkError && typeof window !== 'undefined') {
            const now = Date.now();
            const lastReload = parseInt(sessionStorage.getItem('omnivaso_chunk_reload_lock') || '0', 10);
            if (now - lastReload > 10000) {
                sessionStorage.setItem('omnivaso_chunk_reload_lock', String(now));
                window.location.reload();
                return { hasError: false, error: null };
            }
        }

        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        const msg = (error && error.message) || '';
        const stack = (error && error.stack) || '';
        if (msg.includes('startTime') || msg.includes('reportAllChanges') || stack.includes('reportAllChanges')) {
            return;
        }
        console.error('RootErrorBoundary caught an error:', error, errorInfo);
    }
    handleRecover = () => {
        this.setState({ hasError: false, error: null });
        const appBase = getAppBase();
        window.location.href = appBase.endsWith('/') ? appBase : `${appBase}/`;
    };
    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    minHeight: '80vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2rem',
                    textAlign: 'center',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    color: '#1e293b'
                }}>
                    <div style={{
                        maxWidth: '520px',
                        width: '100%',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '16px',
                        padding: '2rem',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                        <h2 style={{ fontSize: '1.4rem', fontWeight: '700', marginBottom: '0.75rem', color: '#0f172a' }}>
                            Something went wrong
                        </h2>
                        <p style={{ fontSize: '0.92rem', color: '#64748b', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                            An unexpected interface issue occurred. Your data and settings are safely preserved.
                        </p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button
                                type="button"
                                onClick={() => this.setState({ hasError: false, error: null })}
                                style={{
                                    padding: '10px 18px',
                                    borderRadius: '8px',
                                    background: '#f1f5f9',
                                    color: '#334155',
                                    border: '1px solid #cbd5e1',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Try Again
                            </button>
                            <button
                                type="button"
                                onClick={this.handleRecover}
                                style={{
                                    padding: '10px 18px',
                                    borderRadius: '8px',
                                    background: '#3b82f6',
                                    color: '#ffffff',
                                    border: 'none',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Return to Storefront
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

function TenantSlugRedirect() {
    const location = useLocation();
    const match = location.pathname.match(/^\/?(?:sites|s|site)\/[a-z0-9_-]+(\/.*)?$/i);
    const cleanSubPath = match && match[1] ? match[1] : '/';
    return <Navigate to={cleanSubPath} replace />;
}

function DemoModeSync({ isDemoMode, setIsDemoMode, setIsAuthenticated, config }) {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(location.search);
        const demoVal = params.get('demo');

        if (demoVal === 'false' || demoVal === '0' || demoVal === 'off') {
            try {
                localStorage.removeItem('omnivaso_demo_mode');
                localStorage.removeItem('omnivaso_demo_draft');
                sessionStorage.removeItem('omnivaso_demo_mode');
                sessionStorage.removeItem('omnivaso_demo_draft');
            } catch (e) {}
            if (isDemoMode) setIsDemoMode(false);
            params.delete('demo');
            const newSearch = params.toString() ? `?${params.toString()}` : '';
            const newUrl = location.pathname + newSearch + location.hash;
            navigate(newUrl, { replace: true });
            return;
        }

        const hasDemoParam = demoVal === 'true';

        if (hasDemoParam || localStorage.getItem('omnivaso_demo_mode') === 'true') {
            try {
                localStorage.setItem('omnivaso_demo_mode', 'true');
                if (config && !localStorage.getItem('omnivaso_demo_draft')) {
                    localStorage.setItem('omnivaso_demo_draft', JSON.stringify(config));
                }
            } catch (e) { }
            if (!isDemoMode) {
                setIsDemoMode(true);
            }
            setIsAuthenticated(true);

            if (hasDemoParam) {
                params.delete('demo');
                const newSearch = params.toString() ? `?${params.toString()}` : '';
                const newUrl = location.pathname + newSearch + location.hash;
                navigate(newUrl, { replace: true });
            }
        }
    }, [location.search, location.pathname, location.hash, isDemoMode, setIsDemoMode, setIsAuthenticated, config, navigate]);

    return null;
}


function TenantLinkInterceptor() {
    const navigate = useNavigate();

    useEffect(() => {
        const handleGlobalLinkClick = (e) => {
            if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.defaultPrevented) {
                return;
            }

            const anchor = e.target.closest('a[href]');
            if (!anchor) return;

            const isInsideContentEditable = anchor.closest('[contenteditable="true"]');
            if (isInsideContentEditable) {
                e.preventDefault();
                return;
            }

            if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;

            const rawHref = anchor.getAttribute('href');
            if (!rawHref || typeof rawHref !== 'string') return;

            const trimmedHref = rawHref.trim();
            if (!trimmedHref || trimmedHref.startsWith('javascript:') || trimmedHref.startsWith('mailto:') || trimmedHref.startsWith('tel:')) {
                return;
            }

            if (trimmedHref.startsWith('#')) {
                const hashId = trimmedHref.replace(/^#/, '');
                if (hashId) {
                    e.preventDefault();
                    if (typeof window !== 'undefined') {
                        window.history.pushState(null, '', `${window.location.pathname}${window.location.search}#${hashId}`);
                    }
                    smoothScrollTo(hashId);
                }
                return;
            }

            const origin = typeof window !== 'undefined' ? window.location.origin : '';
            const isInternal = (
                trimmedHref.startsWith('/') ||
                trimmedHref.startsWith('./') ||
                trimmedHref.startsWith('../') ||
                (origin && trimmedHref.startsWith(origin)) ||
                trimmedHref.includes('omnivaso.com')
            );

            if (!isInternal) return;

            const cleanSubPath = normalizeAppUrl(trimmedHref);
            if (!cleanSubPath) return;

            if (trimmedHref.includes('demo=true') || trimmedHref.includes('demo=')) {
                try {
                    localStorage.setItem('omnivaso_demo_mode', 'true');
                } catch (e) { }
            }


            if (cleanSubPath.includes('#')) {
                const [pathPart, hashPart] = cleanSubPath.split('#');
                e.preventDefault();
                navigate(pathPart || '/', { state: { targetId: hashPart } });
                return;
            }

            e.preventDefault();
            navigate(cleanSubPath);
        };

        document.addEventListener('click', handleGlobalLinkClick, true);
        return () => document.removeEventListener('click', handleGlobalLinkClick, true);
    }, [navigate]);

    return null;
}

// --- MAIN APP COMPONENT ---
function App() {
    // Demo Mode State Initialization
    const [isDemoMode, setIsDemoMode] = useState(() => {
        if (typeof window === 'undefined') return false;
        const params = new URLSearchParams(window.location.search);
        const demoVal = params.get('demo');
        if (demoVal === 'false' || demoVal === '0' || demoVal === 'off') {
            try {
                localStorage.removeItem('omnivaso_demo_mode');
                localStorage.removeItem('omnivaso_demo_draft');
            } catch (e) {}
            return false;
        }
        if (demoVal === 'true') {
            try {
                localStorage.setItem('omnivaso_demo_mode', 'true');
            } catch (e) { }
            return true;
        }
        return localStorage.getItem('omnivaso_demo_mode') === 'true';
    });

    // Initial State: Instant 0ms boot from cached local storage
    const [config, setConfig] = useState(() => {
        try {
            const isDemo = typeof window !== 'undefined' && localStorage.getItem('omnivaso_demo_mode') === 'true';
            if (isDemo) {
                const demoDraft = localStorage.getItem('omnivaso_demo_draft');
                if (demoDraft) {
                    const parsed = JSON.parse(demoDraft);
                    if (parsed && typeof parsed === 'object' && (parsed.settings || parsed.sections || parsed.pages)) {
                        return mergeConfig(DEFAULT_CONFIG, parsed);
                    }
                }
            }
            const tenant = getTenantHandle();
            const tenantKey = getTenantStorageKey('cached_config');
            const cached = localStorage.getItem(tenantKey) || (!tenant ? localStorage.getItem('omnivaso_cached_config') : null) || localStorage.getItem('omnivaso_demo_draft');
            if (cached) {
                const parsed = JSON.parse(cached);
                if (parsed && typeof parsed === 'object' && (parsed.settings || parsed.sections || parsed.pages)) {
                    const titleUpper = (parsed?.settings?.siteTitle || '').toUpperCase();
                    const isGenericDefault = titleUpper === 'OMNIVASO' || titleUpper === 'OMNIVASO ENGINE';
                    if (!tenant || !isGenericDefault) {
                        return mergeConfig(DEFAULT_CONFIG, parsed);
                    }
                }
            }
            const vault = getLocalVaultSnapshots(tenant);
            if (vault && vault.length > 0 && vault[0].config) {
                return mergeConfig(DEFAULT_CONFIG, vault[0].config);
            }
        } catch (e) { }
        return null;
    });

    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        if (typeof window === 'undefined') return false;
        const params = new URLSearchParams(window.location.search);
        const isDemo = params.get('demo') === 'true' || localStorage.getItem('omnivaso_demo_mode') === 'true';
        return !!localStorage.getItem('adminToken') || isDemo;
    });
    const [toast, setToast] = useState(null);
    const [isGlobalClaimOpen, setIsGlobalClaimOpen] = useState(() => {
        if (typeof window !== 'undefined') {
            const hasSearch = window.location.search.includes('claim=true') || window.location.search.includes('claim');
            const hasHash = window.location.hash === '#claim' || window.location.hash === '#/claim';
            return hasSearch || hasHash;
        }
        return false;
    });
    const demoBarRef = useRef(null);

    useEffect(() => {
        const handleDemoSync = () => {
            if (typeof window !== 'undefined') {
                const params = new URLSearchParams(window.location.search);
                if (params.get('demo') === 'true' || localStorage.getItem('omnivaso_demo_mode') === 'true') {
                    setIsDemoMode(true);
                    setIsAuthenticated(true);
                }
            }
        };
        handleDemoSync();
        window.addEventListener('storage', handleDemoSync);
        return () => window.removeEventListener('storage', handleDemoSync);
    }, []);

    useEffect(() => {
        const checkClaimTrigger = () => {
            if (typeof window !== 'undefined') {
                const hasSearch = window.location.search.includes('claim=true') || window.location.search.includes('claim');
                const hasHash = window.location.hash === '#claim' || window.location.hash === '#/claim';
                if (hasSearch || hasHash) {
                    setIsGlobalClaimOpen(true);
                }
            }
        };
        checkClaimTrigger();
        window.addEventListener('popstate', checkClaimTrigger);
        window.addEventListener('hashchange', checkClaimTrigger);
        return () => {
            window.removeEventListener('popstate', checkClaimTrigger);
            window.removeEventListener('hashchange', checkClaimTrigger);
        };
    }, []);

    const updateAndCacheConfig = (rawConfig) => {
        const merged = mergeConfig(DEFAULT_CONFIG, rawConfig);
        setConfig(merged);
        try {
            const isDemo = localStorage.getItem('omnivaso_demo_mode') === 'true';
            if (isDemo) {
                localStorage.setItem('omnivaso_demo_draft', JSON.stringify(rawConfig));
            }
            const tenant = getTenantHandle();
            localStorage.setItem(getTenantStorageKey('cached_config'), JSON.stringify(rawConfig));
            if (!tenant) {
                localStorage.setItem('omnivaso_cached_config', JSON.stringify(rawConfig));
            }
            saveToLocalVault(tenant, rawConfig, null, null, null, 'system');
        } catch (e) { }
    };

    const fetchConfig = async () => {
        const tenant = getTenantHandle();
        try {
            const targetUrl = getApiUrl('api.php');
            const res = await axios.get(`${targetUrl}${targetUrl.includes('?') ? '&' : '?'}t=${Date.now()}`, {
                headers: tenant ? { 'X-Tenant': tenant } : {}
            });
            if (res.data && typeof res.data === 'object' && (res.data.settings || res.data.sections || res.data.pages)) {
                // If a tenant site is requested and the server returned the uninitialized generic default template,
                // check if the browser has custom cached tenant data to prevent overwriting with the default page.
                const titleUpper = (res.data.settings?.siteTitle || '').toUpperCase();
                const isGenericDefault = titleUpper === 'OMNIVASO' || titleUpper === 'OMNIVASO ENGINE';
                if (tenant && isGenericDefault) {
                    try {
                        const directRes = await axios.get(`/sites/${tenant}/data.json?t=${Date.now()}`);
                        if (directRes.data && typeof directRes.data === 'object' && (directRes.data.settings || directRes.data.sections || directRes.data.pages)) {
                            const directTitle = (directRes.data.settings?.siteTitle || '').toUpperCase();
                            if (directTitle && directTitle !== 'OMNIVASO' && directTitle !== 'OMNIVASO ENGINE') {
                                updateAndCacheConfig(directRes.data);
                                return;
                            }
                        }
                    } catch (e) { }

                    const tenantKey = getTenantStorageKey('cached_config');
                    const cached = localStorage.getItem(tenantKey);
                    if (cached) {
                        try {
                            const parsed = JSON.parse(cached);
                            const cachedTitle = (parsed?.settings?.siteTitle || '').toUpperCase();
                            if (cachedTitle && cachedTitle !== 'OMNIVASO' && cachedTitle !== 'OMNIVASO ENGINE') {
                                updateAndCacheConfig(parsed);
                                return;
                            }
                        } catch (err) { }
                    }
                }
                try {
                    const tKey = getTenantStorageKey('cached_config');
                    const cachedRaw = localStorage.getItem(tKey) || localStorage.getItem('omnivaso_cached_config');
                    if (cachedRaw) {
                        const parsedCached = JSON.parse(cachedRaw);
                        if (res.data.pages === undefined || res.data.pages === null) {
                            if (Array.isArray(parsedCached?.pages)) {
                                res.data.pages = parsedCached.pages;
                            }
                        }
                    }
                } catch (e) { }
                updateAndCacheConfig(res.data);
                return;
            }
        } catch (e) { }

        // If a tenant site is requested and we have valid cached tenant data, preserve it without overwriting with root data.json
        if (tenant) {
            try {
                const tenantKey = getTenantStorageKey('cached_config');
                const cached = localStorage.getItem(tenantKey);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    if (parsed && typeof parsed === 'object' && (parsed.settings || parsed.sections || parsed.pages)) {
                        updateAndCacheConfig(parsed);
                        return;
                    }
                }
            } catch (e) { }

            try {
                const directRes = await axios.get(`/sites/${tenant}/data.json?t=${Date.now()}`);
                if (directRes.data && typeof directRes.data === 'object' && (directRes.data.settings || directRes.data.sections || directRes.data.pages)) {
                    updateAndCacheConfig(directRes.data);
                    return;
                }
            } catch (e) { }
        } else {
            try {
                const cachedRaw = localStorage.getItem('omnivaso_cached_config') || localStorage.getItem(getTenantStorageKey('cached_config'));
                if (cachedRaw) {
                    const parsed = JSON.parse(cachedRaw);
                    if (parsed && typeof parsed === 'object' && (parsed.settings || parsed.sections || parsed.pages)) {
                        updateAndCacheConfig(parsed);
                        return;
                    }
                }
            } catch (e) { }

            try {
                const base = getAppBase();
                const jsonPath = base ? `${base}/data.json` : '/data.json';
                const jsonRes = await axios.get(`${jsonPath}?t=${Date.now()}`);
                if (jsonRes.data && typeof jsonRes.data === 'object' && (jsonRes.data.settings || jsonRes.data.sections || jsonRes.data.pages)) {
                    updateAndCacheConfig(jsonRes.data);
                    return;
                }
            } catch (err) { }
        }

        try {
            const base = getAppBase();
            const masterPath = base ? `${base}/data.master.json` : '/data.master.json';
            const masterRes = await axios.get(`${masterPath}?t=${Date.now()}`);
            if (masterRes.data && typeof masterRes.data === 'object' && (masterRes.data.settings || masterRes.data.sections || masterRes.data.pages)) {
                updateAndCacheConfig(masterRes.data);
                return;
            }
        } catch (err) { }

        if (!tenant) {
            try {
                const sampleRes = await axios.get(`/data.sample.json?t=${Date.now()}`);
                if (sampleRes.data && typeof sampleRes.data === 'object') {
                    updateAndCacheConfig(sampleRes.data);
                    return;
                }
            } catch (err) { }
        }

        try {
            const localVault = getLocalVaultSnapshots(getTenantHandle());
            if (localVault.length > 0 && localVault[0].config) {
                console.info('🛡️ Recovered site configuration from local browser emergency vault');
                updateAndCacheConfig(localVault[0].config);
                return;
            }
        } catch (e) { }

        setConfig(DEFAULT_CONFIG);
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const rawPath = window.location.pathname;
            const cleanedPath = rawPath.replace(/^(?:\/(?:sites|s|site)\/[a-z0-9_-]+)+/i, (match) => {
                const single = match.match(/^\/(?:sites|s|site)\/[a-z0-9_-]+/i);
                return single ? single[0] : match;
            });
            if (cleanedPath !== rawPath) {
                window.location.replace(cleanedPath + window.location.search + window.location.hash);
                return;
            }
        }

        const hash = window.location.hash;
        if (hash && hash.startsWith('#/')) {
            const cleanPath = hash.substring(1);
            const base = getAppBase();
            let target = cleanPath;
            if (base && target.startsWith(base)) {
                target = target.substring(base.length);
            }
            window.location.replace(window.location.origin + base + target);
            return;
        }

        fetchConfig();

        const handleGlobalFocus = (e) => {
            if (!e.target) return;
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                const val = e.target.value;
                if (isPlaceholderText(val)) {
                    e.target.select();
                }
            }
        };

        document.addEventListener('focus', handleGlobalFocus, true);
        return () => {
            document.removeEventListener('focus', handleGlobalFocus, true);
        };
    }, []);

    const handleLogin = (token, role, username) => {
        const tKey = getTenantStorageKey('adminToken');
        localStorage.setItem(tKey, token);
        localStorage.setItem('adminToken', token);
        localStorage.setItem(getTenantStorageKey('userRole'), role);
        localStorage.setItem('userRole', role);
        localStorage.setItem(getTenantStorageKey('loggedInUser'), username || 'admin');
        localStorage.setItem('loggedInUser', username || 'admin');
        try {
            localStorage.removeItem('omnivaso_demo_mode');
            localStorage.removeItem('omnivaso_demo_draft');
            sessionStorage.removeItem('omnivaso_demo_mode');
            sessionStorage.removeItem('omnivaso_demo_draft');
        } catch (e) {}
        setIsDemoMode(false);
        setIsAuthenticated(true);
    };

    const handleLogout = () => {
        try {
            const tenantKey = getTenantStorageKey('cached_config');
            localStorage.removeItem(tenantKey);
            localStorage.removeItem('omnivaso_cached_config');
            localStorage.removeItem(getTenantStorageKey('adminToken'));
            localStorage.removeItem('adminToken');
            localStorage.removeItem(getTenantStorageKey('userRole'));
            localStorage.removeItem('userRole');
            localStorage.removeItem(getTenantStorageKey('loggedInUser'));
            localStorage.removeItem('loggedInUser');
            localStorage.removeItem('omnivaso_demo_mode');
            localStorage.removeItem('omnivaso_demo_draft');
            localStorage.removeItem('omnivaso_live_edit_auto');
            sessionStorage.clear();

            if (typeof document !== 'undefined') {
                document.documentElement.style.removeProperty('--demo-bar-height');
                document.documentElement.style.setProperty('--demo-bar-height', '0px');
                document.body.style.paddingTop = '0px';
                document.body.style.removeProperty('padding-top');
            }
        } catch (e) { }
        setIsDemoMode(false);
        setIsAuthenticated(false);
        if (typeof window !== 'undefined') {
            const cleanUrl = window.location.origin + window.location.pathname + window.location.hash;
            window.location.replace(cleanUrl);
        }
    };

    const saveConfig = async (newData, options = {}) => {
        if (!newData || typeof newData !== 'object') return false;

        // Guard against accidental section / page / article wiping
        const currentSections = Array.isArray(config?.sections) ? config.sections : [];
        const currentPages = Array.isArray(config?.pages) ? config.pages : [];
        const currentArticles = Array.isArray(config?.articles) ? config.articles : [];
        const currentFooter = Array.isArray(config?.footer) ? config.footer : [];

        let mergedData = {
            ...(config || {}),
            ...newData,
            settings: {
                ...(config?.settings || {}),
                ...(newData?.settings || {})
            },
            typography: {
                ...(config?.typography || {}),
                ...(newData?.typography || {})
            },
            hero: {
                ...(config?.hero || {}),
                ...(newData?.hero || {})
            },
            sections: (Array.isArray(newData.sections) && (newData.sections.length > 0 || options?.allowEmptySections))
                ? newData.sections
                : (currentSections.length > 0 ? currentSections : (newData.sections || [])),
            pages: (Array.isArray(newData.pages) && (newData.pages.length > 0 || options?.allowEmptyPages))
                ? newData.pages
                : (currentPages.length > 0 ? currentPages : (newData.pages || [])),
            articles: (Array.isArray(newData.articles) && (newData.articles.length > 0 || options?.allowEmptyArticles))
                ? newData.articles
                : (currentArticles.length > 0 ? currentArticles : (newData.articles || [])),
            footer: (Array.isArray(newData.footer) && (newData.footer.length > 0 || options?.allowEmptyFooter))
                ? newData.footer
                : (currentFooter.length > 0 ? currentFooter : (newData.footer || []))
        };

        // Ensure ALL modules are gathered, merged, and preserved from DEFAULT_CONFIG, current config, cached localStorage, and newData
        const defaultModules = DEFAULT_CONFIG?.modules || {};
        const currentModules = (config && typeof config === 'object' && config.modules && typeof config.modules === 'object') ? config.modules : {};
        let cachedModules = {};
        try {
            const tenantKey = getTenantStorageKey('cached_config');
            const rawCached = localStorage.getItem(tenantKey) || localStorage.getItem('omnivaso_cached_config');
            if (rawCached) {
                const parsed = JSON.parse(rawCached);
                if (parsed && typeof parsed === 'object' && parsed.modules && typeof parsed.modules === 'object') {
                    cachedModules = parsed.modules;
                }
            }
        } catch (e) { }

        const incomingModules = (newData && typeof newData === 'object' && newData.modules && typeof newData.modules === 'object') ? newData.modules : {};

        const allModuleKeys = Array.from(new Set([
            ...Object.keys(defaultModules),
            ...Object.keys(cachedModules),
            ...Object.keys(currentModules),
            ...Object.keys(incomingModules)
        ]));

        const mergedModules = {};
        for (const k of allModuleKeys) {
            const d = defaultModules[k];
            const ca = cachedModules[k];
            const c = currentModules[k];
            const inc = incomingModules[k];

            if (
                (d && typeof d === 'object' && !Array.isArray(d)) ||
                (ca && typeof ca === 'object' && !Array.isArray(ca)) ||
                (c && typeof c === 'object' && !Array.isArray(c)) ||
                (inc && typeof inc === 'object' && !Array.isArray(inc))
            ) {
                mergedModules[k] = {
                    ...(d && typeof d === 'object' && !Array.isArray(d) ? d : {}),
                    ...(c && typeof c === 'object' && !Array.isArray(c) ? c : {}),
                    ...(ca && typeof ca === 'object' && !Array.isArray(ca) ? ca : {}),
                    ...(inc && typeof inc === 'object' && !Array.isArray(inc) ? inc : {})
                };
            } else {
                mergedModules[k] = inc !== undefined ? inc : (ca !== undefined ? ca : (c !== undefined ? c : d));
            }
        }

        mergedData.modules = mergedModules;

        // Always sanitize to strip Gutenberg WAF-triggering comment vectors and corrupted unicode
        let finalDataToSave = sanitizeConfigData(mergedData);

        if (!options?.skipQuotaCheck) {
            const quotaResult = consumeEditQuota(finalDataToSave);
            if (!quotaResult.allowed) {
                const enteredCode = window.prompt(`${quotaResult.message}\n\n🔑 Enter Admin Unlock Code (e.g. OMNIVASO-UNLIMITED) to unlock unlimited edits now:`);
                if (enteredCode) {
                    const redeemResult = redeemAdminPasscode(finalDataToSave, enteredCode);
                    if (redeemResult.success) {
                        alert(redeemResult.message);
                        finalDataToSave = redeemResult.updatedConfig;
                    } else {
                        alert(redeemResult.error);
                        return false;
                    }
                } else {
                    return false;
                }
            } else {
                finalDataToSave = quotaResult.updatedConfig || finalDataToSave;
                if (quotaResult.warningMessage) {
                    setTimeout(() => alert(quotaResult.warningMessage), 200);
                }
            }
        }

        const isDemo = localStorage.getItem('omnivaso_demo_mode') === 'true';
        if (isDemo) {
            try {
                localStorage.setItem('omnivaso_demo_draft', JSON.stringify(finalDataToSave));
                updateAndCacheConfig(finalDataToSave);
                setToast({ message: "🛡️ Sandbox Saved & XSS Sanitized!", type: "success" });
                return true;
            } catch (e) {
                setToast({ message: "❌ Demo save failed", type: "error" });
                return false;
            }
        }

        try {
            const targetUrl = getApiUrl('api.php');
            const res = await axios.post(targetUrl, finalDataToSave, authHeaders());
            if (res.data && res.data.success === false) {
                throw new Error(res.data.message || 'Server rejected configuration save');
            }
            updateAndCacheConfig(finalDataToSave);
            setToast({ message: "✅ Saved Successfully!", type: "success" });
            return true;
        } catch (e) {
            const status = e.response?.status;
            const errMsg = e.response?.data?.message || e.message || 'Server error';

            // Cache locally so user work is never lost in browser
            try {
                localStorage.setItem(getTenantStorageKey('cached_config'), JSON.stringify(finalDataToSave));
                updateAndCacheConfig(finalDataToSave);
            } catch (err) {}

            if (status === 401) {
                setToast({ message: "🔒 Admin session expired. Changes saved in browser only. Please log in to sync.", type: "error" });
            } else if (status === 500) {
                console.error("Save config server error (500):", e);
                setToast({ message: `⚠️ Server error (${errMsg}). Saved in browser cache.`, type: "error" });
            } else {
                console.error("Save config network error:", e);
                setToast({ message: "⚠️ Network offline: Saved locally in browser vault.", type: "warning" });
            }
            return false;
        }
    };

    const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);
    const [isOrderLookupOpen, setIsOrderLookupOpen] = useState(false);

    useEffect(() => {
        const handleOpenSearch = () => setIsGlobalSearchOpen(true);
        window.addEventListener('open-search-tool', handleOpenSearch);
        return () => window.removeEventListener('open-search-tool', handleOpenSearch);
    }, []);

    useEffect(() => {
        const handleOpenOrderLookup = () => setIsOrderLookupOpen(true);
        window.addEventListener('open-order-lookup', handleOpenOrderLookup);
        return () => window.removeEventListener('open-order-lookup', handleOpenOrderLookup);
    }, []);

    useEffect(() => {
        if (!isDemoMode) {
            if (typeof document !== 'undefined') {
                document.documentElement.style.setProperty('--demo-bar-height', '0px');
                document.documentElement.style.removeProperty('--demo-bar-height');
                document.body.style.paddingTop = '0px';
                document.body.style.removeProperty('padding-top');
            }
            return;
        }

        const updateHeight = () => {
            if (typeof window === 'undefined') return;
            window.requestAnimationFrame(() => {
                if (demoBarRef.current && typeof document !== 'undefined') {
                    const h = demoBarRef.current.offsetHeight || 44;
                    document.documentElement.style.setProperty('--demo-bar-height', `${h}px`);
                    document.body.style.paddingTop = `${h}px`;
                }
            });
        };

        updateHeight();
        let ro;
        if (demoBarRef.current && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(updateHeight);
            ro.observe(demoBarRef.current);
        }

        window.addEventListener('resize', updateHeight);
        return () => {
            window.removeEventListener('resize', updateHeight);
            if (ro) ro.disconnect();
            if (typeof document !== 'undefined') {
                document.documentElement.style.setProperty('--demo-bar-height', '0px');
                document.documentElement.style.removeProperty('--demo-bar-height');
                document.body.style.paddingTop = '0px';
                document.body.style.removeProperty('padding-top');
            }
        };
    }, [isDemoMode]);

    if (!config) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#0f172a',
                color: '#ffffff',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
                <div style={{
                    width: '36px',
                    height: '36px',
                    border: '3px solid rgba(255,255,255,0.15)',
                    borderTopColor: '#3b82f6',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    marginBottom: '16px'
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.5px' }}>
                    Loading site configuration...
                </div>
            </div>
        );
    }

    const isEffectiveAuth = isAuthenticated || isDemoMode;
    const ecomUnlocked = isEcommerceUnlocked(config);

    return (
        <BrowserRouter basename={getAppBase()}>
            <DemoModeSync isDemoMode={isDemoMode} setIsDemoMode={setIsDemoMode} setIsAuthenticated={setIsAuthenticated} config={config} />
            <LanguageProvider settings={config?.settings || {}} modules={config?.modules || {}}>
            <CurrencyProvider settings={config?.settings || {}}>
                <CartProvider config={config}>
                    <ScrollToTop />
                    {isDemoMode && (
                        <div
                            ref={demoBarRef}
                            className="omnivaso-demo-topbar"
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                zIndex: 10001,
                                background: 'linear-gradient(90deg, #1e1b4b 0%, #311b92 50%, #1e1b4b 100%)',
                                color: '#ffffff',
                                padding: '6px 14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: '6px 12px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                                borderBottom: '1px solid rgba(255,255,255,0.2)',
                                fontFamily: 'Inter, system-ui, sans-serif',
                                fontSize: '0.82rem',
                                boxSizing: 'border-box'
                            }}
                        >
                            <style>{`
                                @media (max-width: 640px) {
                                    .demo-bar-secondary-hint {
                                        display: none !important;
                                    }
                                    .omnivaso-demo-topbar {
                                        padding: 4px 10px !important;
                                        font-size: 0.76rem !important;
                                    }
                                    .demo-bar-btn {
                                        padding: 4px 8px !important;
                                        font-size: 0.72rem !important;
                                    }
                                }
                            `}</style>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{
                                    background: '#10b981',
                                    color: '#ffffff',
                                    padding: '3px 8px',
                                    borderRadius: '12px',
                                    fontWeight: '800',
                                    fontSize: '0.72rem',
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    boxShadow: '0 0 10px rgba(16, 185, 129, 0.5)',
                                    whiteSpace: 'nowrap'
                                }}>
                                    🎮 DEMO SANDBOX
                                </span>
                                <span className="demo-bar-secondary-hint" style={{
                                    background: 'rgba(59, 130, 246, 0.25)',
                                    border: '1px solid rgba(147, 197, 253, 0.4)',
                                    color: '#93c5fd',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontWeight: '600',
                                    fontSize: '0.72rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    whiteSpace: 'nowrap'
                                }} title="Client-side storage isolation & XSS injection sanitization active">
                                    🛡️ XSS & API Shield
                                </span>
                                <span className="demo-bar-secondary-hint" style={{ color: '#c7d2fe', fontWeight: '500', fontSize: '0.8rem' }}>
                                    Click text or image to edit live!
                                </span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
                                <button
                                    type="button"
                                    className="demo-bar-btn"
                                    onClick={() => {
                                        if (window.confirm('Reset sandbox back to defaults?')) {
                                            localStorage.removeItem('omnivaso_demo_draft');
                                            localStorage.removeItem(getTenantStorageKey('cached_config'));
                                            sessionStorage.clear();
                                            window.location.reload();
                                        }
                                    }}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.15)',
                                        color: '#ffffff',
                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                        borderRadius: '6px',
                                        padding: '4px 10px',
                                        fontSize: '0.78rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    🔄 Reset
                                </button>
                                <button
                                    type="button"
                                    className="demo-bar-btn"
                                    onClick={handleLogout}
                                    style={{
                                        background: '#ef4444',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '4px 10px',
                                        fontSize: '0.78rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    ✕ Exit Demo
                                </button>
                            </div>
                        </div>
                    )}
                    <GlobalStyles config={config} settings={config?.settings} customFonts={config?.customFonts} />
                    <RootErrorBoundary>
                        <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontFamily: 'sans-serif', fontSize: '0.95rem' }}>Loading...</div>}>
                            <Routes>
                                <Route path="/" element={<LandingPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/sites/*" element={<TenantSlugRedirect />} />
                                <Route path="/s/*" element={<TenantSlugRedirect />} />
                                <Route path="/site/*" element={<TenantSlugRedirect />} />
                                <Route path="/shop" element={ecomUnlocked ? <ShopPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} /> : <EcommercePaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/shop/:categorySlug" element={ecomUnlocked ? <ShopPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} /> : <EcommercePaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/products" element={ecomUnlocked ? <ShopPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} /> : <EcommercePaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/products/:categorySlug" element={ecomUnlocked ? <ShopPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} /> : <EcommercePaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/category/:slug" element={ecomUnlocked ? <ShopPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} /> : <EcommercePaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/product/:id" element={ecomUnlocked ? <ProductPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} /> : <EcommercePaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/cart" element={ecomUnlocked ? <CartPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} /> : <EcommercePaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/search" element={<SearchPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/articles" element={<ArticlesPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/articles/:id" element={<ArticleView config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/pages/:slug" element={<PageView config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/why-omnivaso" element={<PromotionalPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/comparison" element={<PromotionalPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                {(() => {
                                    const ticketingUnlocked = isTicketingUnlocked(config);
                                    const customEvtSlug = (config?.modules?.eventTicketing?.slug || '').trim().toLowerCase();
                                    const eventsElement = ticketingUnlocked ? <EventsPage config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} /> : <TicketingPaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />;
                                    return (
                                        <>
                                            <Route path="/events" element={eventsElement} />
                                            <Route path="/tickets" element={eventsElement} />
                                            {Boolean(customEvtSlug && customEvtSlug !== 'events' && customEvtSlug !== 'tickets') && (
                                                <Route path={`/${customEvtSlug}`} element={eventsElement} />
                                            )}
                                        </>
                                    );
                                })()}
                                {(() => {
                                    const artworkUnlocked = isArtworkCatalogUnlocked(config);
                                    const customArtSlug = (config?.modules?.artworkCatalog?.slug || '').trim().toLowerCase();
                                    const artworkElement = artworkUnlocked ? (
                                        <ArtworkCatalogView config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />
                                    ) : (
                                        <ArtworkPaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />
                                    );
                                    const ovrIndexElement = artworkUnlocked ? (
                                        <ViewingRoomIndexPage config={config} isAuthenticated={isEffectiveAuth} />
                                    ) : (
                                        <ArtworkPaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />
                                    );
                                    const ovrSingleElement = artworkUnlocked ? (
                                        <OnlineViewingRoomView config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />
                                    ) : (
                                        <ArtworkPaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />
                                    );
                                    const wallScalerElement = artworkUnlocked ? (
                                        <WallScalerModal isStandalone={true} artwork={config?.modules?.artworkCatalog?.artworks?.[0]} artworks={config?.modules?.artworkCatalog?.artworks} config={config} isAuthenticated={isEffectiveAuth} />
                                    ) : (
                                        <ArtworkPaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />
                                    );
                                    return (
                                        <>
                                            <Route path="/artworks" element={artworkElement} />
                                            <Route path="/artworks/:id" element={artworkElement} />
                                            <Route path="/catalog" element={artworkElement} />
                                            <Route path="/viewing-rooms" element={ovrIndexElement} />
                                            <Route path="/viewing-rooms/:slug" element={ovrSingleElement} />
                                            <Route path="/ovr" element={ovrIndexElement} />
                                            <Route path="/ovr/:slug" element={ovrSingleElement} />
                                            <Route path="/wall-scaler" element={wallScalerElement} />
                                            <Route path="/artworks/wall-scaler" element={wallScalerElement} />
                                            {Boolean(customArtSlug && customArtSlug !== 'artworks' && customArtSlug !== 'catalog') && (
                                                <>
                                                    <Route path={`/${customArtSlug}`} element={artworkElement} />
                                                    <Route path={`/${customArtSlug}/:id`} element={artworkElement} />
                                                </>
                                            )}
                                        </>
                                    );
                                })()}
                                <Route path="/track-order" element={<TrackOrderRoute setIsOrderLookupOpen={setIsOrderLookupOpen} />} />
                                <Route path="/order-lookup" element={<TrackOrderRoute setIsOrderLookupOpen={setIsOrderLookupOpen} />} />
                                <Route path="/my-account" element={ecomUnlocked ? <CustomerAccountRedirect /> : <EcommercePaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/account" element={ecomUnlocked ? <CustomerAccountRedirect /> : <EcommercePaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/my-orders" element={ecomUnlocked ? <CustomerAccountRedirect /> : <EcommercePaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/orders" element={ecomUnlocked ? <CustomerAccountRedirect /> : <EcommercePaywall config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="/login" element={isAuthenticated && !isDemoMode ? <Navigate to="/admin" /> : <LoginPage onLogin={handleLogin} />} />
                                <Route path="/agency-login" element={isAuthenticated && !isDemoMode && localStorage.getItem('userRole') === 'superuser' ? <Navigate to="/admin?tab=agency" /> : <AgencyLoginPage onLogin={handleLogin} />} />
                                <Route path="/agency/login" element={isAuthenticated && !isDemoMode && localStorage.getItem('userRole') === 'superuser' ? <Navigate to="/admin?tab=agency" /> : <AgencyLoginPage onLogin={handleLogin} />} />
                                <Route path="/super-login" element={isAuthenticated && !isDemoMode && localStorage.getItem('userRole') === 'superuser' ? <Navigate to="/admin?tab=agency" /> : <AgencyLoginPage onLogin={handleLogin} />} />
                                <Route path="/admin" element={isAuthenticated && !isDemoMode ? <AdminDashboard config={config} onUpdate={(savedCfg) => { if (savedCfg && typeof savedCfg === 'object') { updateAndCacheConfig(savedCfg); } else { fetchConfig(); } }} onLogout={handleLogout} /> : <Navigate to="/login" />} />
                                <Route path="/dashboard" element={isAuthenticated && !isDemoMode ? <AdminDashboard config={config} onUpdate={(savedCfg) => { if (savedCfg && typeof savedCfg === 'object') { updateAndCacheConfig(savedCfg); } else { fetchConfig(); } }} onLogout={handleLogout} /> : <Navigate to="/login" />} />
                                <Route path="/:slug" element={<PageView config={config} onSaveConfig={saveConfig} isAuthenticated={isEffectiveAuth} />} />
                                <Route path="*" element={<NotFoundPage config={config} />} />
                            </Routes>
                        </Suspense>
                    </RootErrorBoundary>
                    {Boolean(config?.settings?.showSearchFab && config?.settings?.searchFabPosition !== 'hidden') && (
                        <button
                            type="button"
                            onClick={() => setIsGlobalSearchOpen(true)}
                            title="Quick Search (⌘K)"
                            style={{
                                position: 'fixed',
                                bottom: '24px',
                                right: config?.settings?.searchFabPosition === 'bottom-left' ? 'auto' : '24px',
                                left: config?.settings?.searchFabPosition === 'bottom-left' ? '24px' : 'auto',
                                zIndex: 9990,
                                background: config?.settings?.searchAccentColor || config?.settings?.primaryColor || '#3b82f6',
                                color: '#ffffff',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                borderRadius: '50px',
                                padding: '12px 20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                fontWeight: 'bold',
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
                            }}
                            onMouseOver={e => {
                                e.currentTarget.style.transform = 'scale(1.06) translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 15px 30px rgba(0,0,0,0.4)';
                            }}
                            onMouseOut={e => {
                                e.currentTarget.style.transform = 'none';
                                e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.3)';
                            }}
                        >
                            <span style={{ fontSize: '1.2rem' }}>🔍</span>
                            <span>{config?.settings?.searchFabLabel || 'Search'}</span>
                        </button>
                    )}

                    <SearchTool
                        config={config}
                        isOpen={isGlobalSearchOpen}
                        onClose={() => setIsGlobalSearchOpen(false)}
                        onSetConfig={setConfig}
                        onSaveConfig={saveConfig}
                    />
                    <SubdomainProvisioner
                        isOpen={isGlobalClaimOpen}
                        onClose={() => setIsGlobalClaimOpen(false)}
                    />
                    <OrderLookupModal
                        isOpen={isOrderLookupOpen}
                        onClose={() => setIsOrderLookupOpen(false)}
                    />
                    <TenantLinkInterceptor />
                    <AgeVerificationOverlay config={config} />
                    {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
                </CartProvider>
            </CurrencyProvider>
            </LanguageProvider>
        </BrowserRouter>
    );
}

export default App;
